/**
 * ClientesPresupuestos — Vista de presupuestos en el portal del cliente
 * Ruta: /clientes/presupuestos
 * Muestra los presupuestos vinculados al contacto del cliente autenticado.
 */
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Card } from '@/components/ui/card';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { EmptyState } from '@/components/common/EmptyState';
import { QuoteStatusBadge } from '@/components/billing/QuoteStatusBadge';
import { useQuotes } from '@/hooks/useBilling';
import { useAuth } from '@/providers/AuthProvider';

function formatCents(cents: number): string {
  return (cents / 100).toFixed(2) + ' €';
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  });
}

export function ClientesPresupuestos() {
  const navigate = useNavigate();
  const { t } = useTranslation('portal');
  const { user } = useAuth();
  const { data, isLoading } = useQuotes({
    contact_id: (user as { contact_id?: string })?.contact_id,
    limit: 50,
  });

  const quotes = data?.items || [];

  return (
    <div className="p-4 space-y-4">
      <h1 className="text-xl font-bold text-gray-900">{t('quotes.title')}</h1>

      {isLoading && (
        <div className="flex justify-center py-12">
          <LoadingSpinner />
        </div>
      )}

      {!isLoading && quotes.length === 0 && (
        <EmptyState
          title={t('quotes.noQuotes')}
          description={t('quotes.noQuotesDesc')}
        />
      )}

      <div className="space-y-3">
        {quotes.map((quote) => (
          <Card
            key={quote.id}
            className="p-4 cursor-pointer hover:shadow-md transition-shadow active:bg-gray-50"
            onClick={() => {
              if (quote.public_token) {
                navigate(`/presupuesto/${quote.public_token}`);
              }
            }}
          >
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <span className="text-sm font-semibold text-gray-900">
                    {quote.quote_number}
                  </span>
                  <QuoteStatusBadge status={quote.status} />
                </div>
                <p className="text-xs text-gray-500">{formatDate(quote.created_at)}</p>
              </div>
              <p className="text-sm font-semibold text-gray-900 shrink-0">
                {formatCents(quote.total_cents)}
              </p>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}
